import http from '@/apis/utils/request-plugin';
import { LoginTerminal } from '@/configs/enums/basics';

const baseUrl = '/login';
const baseUrlForForgotPwd = '/forgot-password';
const baseUrlForWx = '/login/we-chat';

/**
 * 密码验证接口
 *
 * @param data 请求参数对象
 * @param data.account 登录名/手机号
 * @param data.appId 应用系统标识
 * @param data.password 密码
 * @returns 返回http.postJson调用的结果
 */
const password = (data: {
  /**
   * 登录名/手机号
   */
  account: null | string;
  /**
   * 应用系统标识
   */
  appId: null | string;
  /**
   * 密码
   */
  password: null | string;
}) =>
  http.postJson(`${baseUrl}/password`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

/**
 * 发送验证码
 * @param {object} data 发送验证码信息
 * @param {string} data.phone 手机号
 * @param {number} data.userType 用户类型
 * @returns
 */
const sendCode = (data: {
  /**
   * 手机号
   */
  phone: string;
  /**
   * 用户类型
   */
  userType: number;
}) =>
  http.postPreset(`${baseUrl}/send-code`, data, {
    tokenType: 'none',
  });

/**
 * 发送验证码
 * @param {object} data 发送验证码信息
 * @param {string} data.phone 手机号
 * @param {number} data.userType 用户类型
 * @returns
 */
export const sendCodeForWxBind = (data: {
  /**
   * 手机号
   */
  phone: string;
  captchaTicket: string;
}) =>
  http.postPreset(`${baseUrl}/send-code-for-bind-id-card`, data, {
    tokenType: 'none',
  });

const sendCodeForForgotPwd = (data: {
  /**
   * 向指定用户发送忘记密码的验证码
   *
   * @param data 包含用户手机号的对象
   * @param data.phone 用户手机号
   * @returns 返回发送验证码的响应结果
   */
  /**
   * 手机号
   */
  phone: string;
}) =>
  http.postPreset(`${baseUrlForForgotPwd}/send-code`, data, {
    tokenType: 'none',
  });

/**
 * 检查验证码是否正确
 * @param {Object} data 验证码信息
 * @param {string} data.code 验证码
 * @param {string} data.phone 手机号
 * @param {number} data.userType 用户类型
 * @returns
 */
const checkCode = (data: {
  /**
   * 验证码
   */
  code: string;
  /**
   * 手机号
   */
  phone: string;
  /**
   * 用户类型
   */
  userType: number;
}) =>
  http.postPreset(`${baseUrl}/check-code`, data, {
    tokenType: 'none',
  });

/**
 * 检查验证码是否正确(忘记密码)
 * @param {Object} data 验证码信息
 * @param {string} data.code 验证码
 * @param {string} data.phone 手机号
 * @param {number} data.userType 用户类型
 * @returns
 */
const checkCodeForForgotPwd = (data: {
  /**
   * 验证码
   */
  code: string;
  /**
   * 手机号
   */
  phone: string;
  /**
   * 用户类型
   */
  userType: number;
}) =>
  http.postPreset(`${baseUrlForForgotPwd}/check-code`, data, {
    tokenType: 'none',
  });

/**
 * 修改密码
 * @param {Object} data 修改密码信息
 * @param {string} data.ticket 票据
 * @param {string} data.newPassword 新密码
 * @returns
 */
const modifyPwd = (data: {
  /**
   * 新密码，一次加密
   */
  newPassword: string;
  /**
   * 票据
   */
  ticket: string;
}) =>
  http.postPreset(`${baseUrl}/modify-password`, data, {
    tokenType: 'none',
  });

/**
 * 微信登录
 * @param data
 * @returns
 */
export const weChatLogin = (data: {
  /**
   * 应用系统标识
   */
  appId: null | string;
  /**
   * 授权码
   */
  code: null | string;
  /**
   * 用户类型编码，不为空则会验证登录用户是否拥有该用户类型
   */
  userType?: null | string;
}) =>
  http.postJson(`${baseUrlForWx}`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

/**
 * 微信登录绑定
 * @param data
 * @returns
 */
export const weChatLoginBind = (data: {
  /**
   * 应用系统标识
   */
  appId: null | string;
  /**
   * 身份证号
   */
  idCard: null | string;
  /**
   * 临时票据
   */
  ticket: null | string;
  /**
   * 用户类型编码，不为空则会验证登录用户是否拥有该用户类型
   */
  userType?: null | string;
}) =>
  http.postJson(`${baseUrlForWx}/bind`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

/**
 * 获取企业微信登录可选择的单位列表（公开接口）
 * <p>企业内部开发模式下，前端需先选择企业再构建企业微信登录面板，
 * 本接口返回已配置企微凭证的单位（含 corpId），供前端选择企业。</p>
 * <p>不需要登录态，tokenType: 'none'。</p>
 * @returns 单位简单信息列表（id、单位名、corpId、agentId、logo）
 */
export const getWeComUnitList = () =>
  http.get(`${baseUrl}/we-com/unit-list`, undefined, {
    tokenType: 'none',
  });

/**
 * 企业微信登录
 * @param data
 * @returns
 */
export const weWorkLogin = (data: {
  /**
   * 应用系统标识
   */
  appId: null | string;
  /**
   * 授权码
   */
  code: null | string;
  /**
   * 企业微信corpId
   */
  corpId: null | string;
  /**
   * 用户类型编码，不为空则会验证登录用户是否拥有该用户类型
   */
  userType?: null | string;
}) =>
  http.postJson(`${baseUrl}/we-com`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

export const weComOauth2 = (data: { corpId: string; code: string }) =>
  http.postJson(`${baseUrl}/we-com-oauth2`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

export const weChatOauth2 = (data: { corpId: string; code: string }) =>
  http.postJson(`${baseUrl}/we-chat-oauth2`, data, {
    tokenType: 'none',
    sourceType: LoginTerminal.WEB端,
  });

/**
 * 上报当前企业微信单位
 * <p>前端在企业微信 onShow 或切企业回调里调用，根据 corpId 更新当前活跃单位（不重登）</p>
 *
 * @param data.corpId 企业微信corpId
 */
export const reportCurrentUnit = (data: { corpId: string }) =>
  http.postPreset('/report-current-unit', data);

/**
 * 获取当前账号的密码强度规则（公开接口）
 * <p>用于登录态外的「修改初始密码 / 找回密码」页：动态展示密码要求、动态校验。</p>
 * <p>不需要登录态，tokenType: 'none'。</p>
 * <p>返回结构：{ isEnablePwdLength, pwdLength, pwdComplexities: number[] }</p>
 * <p>pwdComplexities 枚举：1=大写字母 2=小写字母 3=数字 4=特殊符号</p>
 */
export const getPwdSafetyRule = () =>
  http.get(`${baseUrl}/pwd-safety-rule`, undefined, {
    tokenType: 'none',
  });

export {
  password,
  checkCode,
  modifyPwd,
  sendCode,
  sendCodeForForgotPwd,
  checkCodeForForgotPwd,
};
